import type { MessageModel } from 'types/chat/messages/message';
import { IsJsonString } from 'utils/helpers';
import { resolveMessageNumericId } from 'utils/reply-message-id';

const EMOJI_FONT_STACK =
  '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Segoe UI Symbol", sans-serif';

export const MESSAGE_REACTION_EMOJI_FONT_FAMILY = EMOJI_FONT_STACK;

function stripWrappingQuotes(value: string): string {
  const trimmed = value.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

function isLikelyReactionUrl(value: string): boolean {
  return /^https?:\/\//i.test(value) || value.startsWith('data:') || value.includes('/api/');
}

function isDisplayableReactionEmoji(value: string): boolean {
  const cleaned = stripWrappingQuotes(value);
  if (!cleaned || isLikelyReactionUrl(cleaned)) return false;
  return [...cleaned].length <= 8;
}

export function isReactionMessageType(fileType?: string | null): boolean {
  return String(fileType ?? '').trim().toLowerCase() === 'reaction';
}

export function isReactionSocketPayload(
  md: Record<string, unknown>,
  msg?: Pick<MessageModel, 'FileType' | 'Message'> | null,
): boolean {
  if (msg && isReactionMessageType(msg.FileType)) return true;
  const mt = String(md.messageType ?? md.MessageType ?? '').trim().toLowerCase();
  return mt === 'reaction';
}

function parseNumericId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return Number.parseInt(value.trim(), 10);
  return null;
}

/** מזהה ההודעה שעליה הופעלה התגובה באימוג'י */
export function resolveReactionTargetMessageId(
  md: Record<string, unknown>,
  msg: Pick<MessageModel, 'Reply'>,
): number | null {
  const candidates = [
    md.replyMessageId,
    md.ReplyMessageId,
    md.parentMessageId,
    md.ParentMessageId,
    md.repliedToMessageId,
    md.RepliedToMessageId,
    md.replyToMessageId,
    md.ReplyToMessageId,
  ];

  for (const candidate of candidates) {
    const parsed = parseNumericId(candidate);
    if (parsed != null) return parsed;
  }

  const reply = msg.Reply;
  if (reply && typeof reply === 'object') {
    return resolveMessageNumericId(reply);
  }

  return null;
}

function emojiFromReactionEntry(entry: unknown): string | null {
  if (entry == null) return null;

  if (typeof entry === 'string') {
    const cleaned = stripWrappingQuotes(entry);
    return isDisplayableReactionEmoji(cleaned) ? cleaned : null;
  }

  if (typeof entry === 'object' && !Array.isArray(entry)) {
    const o = entry as Record<string, unknown>;
    const raw =
      o.emoji ??
      o.Emoji ??
      o.reaction ??
      o.Reaction ??
      o.message ??
      o.Message ??
      o.text ??
      o.Text;

    if (typeof raw === 'string') {
      const cleaned = stripWrappingQuotes(raw);
      return isDisplayableReactionEmoji(cleaned) ? cleaned : null;
    }

    if (raw != null && typeof raw === 'object') {
      return emojiFromReactionEntry(raw);
    }
  }

  return null;
}

/** מנרמל שדה reactions מהשרת למערך מחרוזות אימוג'י לתצוגה */
export function normalizeMessageReactions(raw: unknown): string[] {
  if (raw == null) return [];

  let parsed: unknown = raw;

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];

    if (IsJsonString(trimmed)) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        const cleaned = stripWrappingQuotes(trimmed);
        return isDisplayableReactionEmoji(cleaned) ? [cleaned] : [];
      }
    } else {
      const cleaned = stripWrappingQuotes(trimmed);
      return isDisplayableReactionEmoji(cleaned) ? [cleaned] : [];
    }
  }

  if (Array.isArray(parsed)) {
    return parsed
      .map((entry) => emojiFromReactionEntry(entry))
      .filter((emoji): emoji is string => Boolean(emoji));
  }

  const single = emojiFromReactionEntry(parsed);
  return single ? [single] : [];
}

export function extractReactionEmojiFromPayload(
  md: Record<string, unknown>,
  msg: Pick<MessageModel, 'Message' | 'reactions'>,
): string | null {
  const fromReactions = normalizeMessageReactions(msg.reactions);
  if (fromReactions.length > 0) return fromReactions[fromReactions.length - 1] ?? null;

  const mdEmoji = md.emoji ?? md.Emoji;
  if (typeof mdEmoji === 'string' && isDisplayableReactionEmoji(mdEmoji)) {
    return stripWrappingQuotes(mdEmoji);
  }

  if (typeof msg.Message === 'string' && isDisplayableReactionEmoji(msg.Message)) {
    return stripWrappingQuotes(msg.Message);
  }

  return null;
}

/** מעדכן שדה reactions על ההודעה המקורית (מחליף לתצוגה את האימוג'י האחרון) */
export function mergeReactionEmoji(raw: unknown, emoji: string): string {
  const cleaned = stripWrappingQuotes(emoji);
  if (!isDisplayableReactionEmoji(cleaned)) {
    return typeof raw === 'string' ? raw : JSON.stringify(normalizeMessageReactions(raw));
  }

  const prev = normalizeMessageReactions(raw).filter((item) => item !== cleaned);
  return JSON.stringify([...prev, cleaned]);
}
